// src/pages/ResendVerificationPage.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import useAuth from "../hooks/useAuth";

const ResendVerificationPage = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuth();

  const successScreenHeadingRef = useRef(null);
  const errorDisplayRef = useRef(null);
  const emailInputRef = useRef(null);


  useEffect(() => {
    if (success && successScreenHeadingRef.current) {
      successScreenHeadingRef.current.focus();
    }
  }, [success]);

  useEffect(() => {
    if (error && errorDisplayRef.current) {
      errorDisplayRef.current.focus();
    }
  }, [error]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setIsLoading(true);

    try {
      // Assuming auth has a resendVerification method
      await auth.resendVerification(email);
      setSuccess(true);
    } catch (err) {
      setError(
        err.details?.message ||
          err.message ||
          "Failed to send verification email. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>

        <div role="status" aria-live="polite" className="relative max-w-md w-full">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                <svg
                  aria-hidden="true"
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 ref={successScreenHeadingRef} tabIndex={-1} className="text-3xl font-bold text-slate-100 mb-2 outline-none"> {/* Contrast Fix, Focus target */}
                Email Sent!
              </h2>
              <p className="text-slate-300"> {/* Contrast Fix */}
                Check your inbox for the verification link
              </p>
            </div>

            <div className="bg-green-700/20 border border-green-600/30 rounded-xl p-4 mb-6"> {/* Contrast Fix */}
              <div className="flex items-start">
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 text-green-300 mr-2 mt-0.5 flex-shrink-0" /* Contrast Fix */
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="text-green-200 text-sm"> {/* Contrast Fix */}
                  <p className="font-medium mb-1">
                    Verification email sent to:
                  </p>
                  <p className="text-green-100">{email}</p> {/* Contrast Fix */}
                </div>
              </div>
            </div>

            <div className="bg-blue-700/20 border border-blue-600/30 rounded-xl p-4 mb-6"> {/* Contrast Fix */}
              <h3 className="text-sky-200 font-medium text-sm mb-2"> {/* Contrast Fix */}
                What to do next:
              </h3>
              <ul className="text-sky-100 text-sm space-y-1"> {/* Contrast Fix */}
                <li className="flex items-start">
                  <span aria-hidden="true" className="w-1.5 h-1.5 bg-sky-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span> {/* Adjusted mt for alignment */}
                  Check your email inbox for the verification link
                </li>
                <li className="flex items-start">
                  <span aria-hidden="true" className="w-1.5 h-1.5 bg-sky-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                  Click the link to verify your account
                </li>
                <li className="flex items-start">
                  <span aria-hidden="true" className="w-1.5 h-1.5 bg-sky-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                  Return to login once verified
                </li>
              </ul>
            </div>

            <div className="bg-yellow-700/20 border border-yellow-600/30 rounded-xl p-4 mb-6"> {/* Contrast Fix */}
              <div className="flex items-start">
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 text-yellow-300 mr-2 mt-0.5 flex-shrink-0" /* Contrast Fix */
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <div className="text-yellow-100 text-sm"> {/* Contrast Fix */}
                  <p className="font-medium mb-1">Don't see the email?</p>
                  <ul className="space-y-1 text-yellow-200"> {/* Contrast Fix */}
                    <li>• Check your spam/junk folder</li>
                    <li>• Check your promotions tab (Gmail)</li>
                    <li>• Wait a few minutes for delivery</li>
                    <li>• Make sure you entered the correct email</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => {
                  setSuccess(false);
                  setEmail("");
                  setTimeout(() => {
                    if (emailInputRef.current) {
                      emailInputRef.current.focus();
                    }
                  }, 0);
                }}
                className="w-full py-3 bg-slate-700/50 border border-slate-600/50 text-slate-200 font-medium rounded-xl hover:bg-slate-600/50 transition-all duration-300" /* Contrast Fix */
              >
                Send Another Email
              </button>

              <Link
                to="/login"
                className="block w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold text-center rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>

      <div className="relative max-w-md w-full">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
              <svg
                aria-hidden="true"
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-slate-100 mb-2"> {/* Contrast Fix */}
              Verify Your Email
            </h2>
            <p className="text-slate-300"> {/* Contrast Fix */}
              Resend verification link to your email
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-slate-200 mb-2" /* Contrast Fix */
              >
                Email Address
              </label>
              <input
                ref={emailInputRef}
                type="email"
                placeholder="Enter your email address"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300" /* Contrast Fix for placeholder */
                required
                autoComplete="email"
              />
            </div>

            {error && (
              <div role="alert" aria-live="assertive" ref={errorDisplayRef} tabIndex={-1} className="bg-red-700/20 border border-red-600/30 rounded-xl p-4 outline-none"> {/* Contrast Fix, ARIA, Focus */}
                <div className="flex items-center">
                  <svg
                    aria-hidden="true"
                    className="w-5 h-5 text-red-300 mr-2" /* Contrast Fix */
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-red-200 text-sm">{error}</p> {/* Contrast Fix */}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg
                    aria-hidden="true"
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending Email...
                </div>
              ) : (
                "Send Verification Email"
              )}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sky-400 hover:text-sky-300 text-sm font-medium hover:underline transition-colors" /* Contrast Fix */
              >
                Back to Login
              </Link>
            </div>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-300"> {/* Contrast Fix */}
              Need help?{" "}
              <Link
                to="/contact"
                className="text-sky-400 hover:text-sky-300 font-semibold hover:underline transition-colors" /* Contrast Fix */
              >
                Contact Support
              </Link>
            </p>
          </div>
        </div>

        {/* Additional info - consider removing or ensuring high contrast if kept */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center space-x-6 text-slate-300 text-sm"> {/* Contrast Fix */}
            <div className="flex items-center">
              <svg
                aria-hidden="true"
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              Secure Process
            </div>
            <div className="flex items-center">
              <svg
                aria-hidden="true"
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Quick Delivery
            </div>
            <div className="flex items-center">
              <svg
                aria-hidden="true"
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              24/7 Support
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResendVerificationPage;
